import { addMinutes, format, getOverlappingDaysInRanges, isWithinRange, parse, addDays } from 'date-fns'
import { getI18n } from '@/libs/i18n'
import { CDN_HOST, DEFAULT_TIMEZONE_NAME } from '@/libs/constants'
import nacl from 'tweetnacl'
import naclUtil from 'tweetnacl-util'
import { convertToLocalTime, convertToTimeZone, formatToTimeZone } from 'date-fns-timezone'
import punycode from 'punycode'
import { unified } from 'unified'
import remarkGfm from 'remark-gfm'
import markdown from 'remark-parse'
import remark2rehype from 'remark-rehype'
import html from 'rehype-stringify'
import rehypePrismPlus from 'rehype-prism-plus'
import rehypeExternalLinks from 'rehype-external-links'
import { visit } from 'unist-util-visit'
import { isInIframe, CommonStorage } from '@/libs/auth-helper'

const URL_PROTOCOL_REGEX = /^https?:\/\//i

export function isAdminId (userId) {
  return userId === 'admin' || userId.slice(-10) === '@4grit.com'
}

export function isAiFeatureHidden (userData) {
  if (!userData) return false

  return userData.userId === 'samsungcard' || userData.userNum === 'b260605e062948u463'
}

export function isString (value) {
  return typeof value === 'string'
}

export let homeUrl = 'http://localhost:9500'
if (process.env.VUE_APP_HOME_URL) {
  homeUrl = process.env.VUE_APP_HOME_URL
}

let beusableUrl = 'https://tool.beusable.net'
if (process.env.VUE_APP_BEUSABLE_URL) {
  beusableUrl = process.env.VUE_APP_BEUSABLE_URL
}

let geoUrl = 'https://geo.beusable.net'
if (process.env.VUE_APP_GEO_URL) {
  geoUrl = process.env.VUE_APP_GEO_URL
}

export const BEUSABLE_URL = beusableUrl
export const GEO_URL = geoUrl

export function onlySubdomain (string, domain) {
  const regex = new RegExp(`\\.*${domain}\\.*`, 'g')
  return string.replace(regex, '').trim()
}

export function uiClearTextSelection () {
  if (window.getSelection) {
    if (window.getSelection().empty) {
      window.getSelection().empty()
    } else if (window.getSelection().removeAllRanges) {
      window.getSelection().removeAllRanges()
    }
  } else if (document.selection) {
    document.selection.empty()
  }
}

export function getFromPageKey (key) {
  const splitedData = key.split('|')
  if (splitedData) {
    const sid = splitedData[0].substr(1)
    const urlId = parseInt(splitedData[1])
    const matchType = splitedData[0].substr(0, 1) === 'e' ? 'exact' : 'simple'
    if (sid && urlId && matchType) {
      return { sid, urlId, matchType }
    }
  }
  return null
}

/**
 * Return the API node key for a journey node.
 *
 * Custom nodes normally use `customId`, but the synthetic root node has only
 * `key: '-1'`. In that case we must preserve `-1` so root requests keep
 * sending `nodeKey`.
 */
export function getJourneyNodeKey (node, isCustomNode = false) {
  if (!node) return null

  if (isCustomNode && node.customId !== undefined && node.customId !== null) {
    return node.customId
  }

  return node.key
}

export function substrByBytes (str, len) {
  let l = 0
  for (let i = 0; i < str.length; i++) {
    l += (new TextEncoder().encode(str.charAt(i))).length
    if (l > len) {
      return str.substring(0, i)
    }
  }
  return str
}

export function isArray (value) {
  return Array.isArray(value)
}

export function isInRanges (needle, ranges) {
  for (const range of ranges) {
    if (isWithinRange(needle, range.start, range.end)) {
      return true
    }
  }
  return false
}

export function isBeforeUnpaid (needle, ranges) {
  for (const range of ranges) {
    if (format(needle, 'YYYY-MM-DD') === format(addDays(range.start, -1), 'YYYY-MM-DD')) {
      return true
    }
  }
}

export function isAfterUnpaid (needle, ranges) {
  for (const range of ranges) {
    if (format(needle, 'YYYY-MM-DD') === format(addDays(range.end, 1), 'YYYY-MM-DD')) {
      return true
    }
  }
}

export function hasOverlapRanges (needle, ranges) {
  const realNeedle = {
    start: addMinutes(needle.start, -1),
    end: addMinutes(needle.end, 1)
  }
  for (const range of ranges) {
    if (getOverlappingDaysInRanges(range.start, range.end, realNeedle.start, realNeedle.end) > 0) {
      return true
    }
  }
  return false
}

export function isPlainObject (value) {
  return Object.prototype.toString.call(value) === '[object Object]'
}

export function isInteger (value) {
  return typeof value === 'number' && isFinite(value) && Math.floor(value) === value
}

export function upperFirst (value) {
  value = String(value)

  if (!value.length) {
    return value
  }
  return value.charAt(0).toUpperCase() + value.slice(1)
}

export function groupBy (arr, key) {
  return arr.reduce((acc, cur) => {
    if (!acc[cur[key]]) {
      acc[cur[key]] = []
    }
    acc[cur[key]].push(cur)
    return acc
  }, {})
}

export function partition (arr, size) {
  return arr.reduce((acc, cur, idx) => {
    if (idx % size === 0) {
      acc.push([cur])
    } else {
      acc[acc.length - 1].push(cur)
    }
    return acc
  }, [])
}

function collectionFn (arr, fieldOrIndex, operation) {
  return arr.reduce((acc, cur, idx) => {
    let value = cur

    if (Array.isArray(cur) && cur[fieldOrIndex] !== undefined) {
      value = cur[fieldOrIndex]
    } else if (typeof cur === 'object' && cur.hasOwnProperty(fieldOrIndex)) {
      value = cur[fieldOrIndex]
    }

    switch (operation) {
      case 'sum':
        if (value === null || !isFinite(value)) {
          value = 0
        }
        return acc + value
      default:
        throw new Error(`invalid operation: ${operation}`)
    }
  }, 0)
}

export function sum (arr, fieldOrIndex) {
  if (!isArray(arr)) {
    return 0
  }
  return collectionFn(arr, fieldOrIndex, 'sum')
}

export function cloneDeep (data) {
  return JSON.parse(JSON.stringify(data))
}

const charToUnicode = (str, lowerCase) => {
  if (!str) return ''
  if (lowerCase) {
    str = str.toLowerCase()
  }

  let unicode = ''
  for (let i = 0, l = str.length; i < l; i++) {
    const uc = str[i].charCodeAt(0).toString(16)
    unicode += '0'.repeat(uc.length > 4 ? 0 : 4 - uc.length) + uc
  }
  return String(unicode)
}

export function compare09aAzZ (a, b) {
  // localeCompare가 mac에서는 0-9 aA-zZ 가-힣, windows에서 0-9 가-힣 aA-zZ가 되어서 아예 유니코드로 순서로 비교함
  return charToUnicode(a.domainName, true).localeCompare(charToUnicode(b.domainName, true))
}

export function compare09azAZ (a, b) {
  const upsidedown = (s) => s.split('').map((c) => c === c.toUpperCase() ? c.toLowerCase() : c.toUpperCase()).join('')
  return charToUnicode(upsidedown(a)).localeCompare(charToUnicode(upsidedown(b)))
}

// https://gist.github.com/beaucharman/e46b8e4d03ef30480d7f4db5a78498ca
export function throttle (callback, delay) {
  let isThrottled = false
  let context, args

  function wrapper () {
    if (isThrottled) {
      args = arguments
      context = this
      return
    }

    isThrottled = true
    callback.apply(this, arguments)

    setTimeout(() => {
      isThrottled = false
      if (args) {
        wrapper.apply(context, args)
        args = context = null
      }
    }, delay)
  }
  return wrapper
}

export function debounce (callback, delay) {
  var timer = null

  return function (...args) {
    const context = this
    clearTimeout(timer)
    timer = setTimeout(() => {
      callback.apply(context, args)
    }, delay)
  }
}

export function pick (obj, keys) {
  const data = {}

  for (const [key, value] of Object.entries(obj)) {
    if (keys.includes(key)) {
      data[key] = value
    }
  }
  return data
}

// https://stackoverflow.com/questions/24586110/resolve-promises-one-after-another-i-e-in-sequence/42324940#42324940
export function sequence (tasks, fn) {
  return tasks.reduce((promise, task) => promise.then(() => fn(task)), Promise.resolve())
}

export function getUrlWithoutProtocol (url) {
  if (!url) {
    return url
  }
  return url.replace(URL_PROTOCOL_REGEX, '')
}

export function getS3Url (pathKey, timestamp) {
  if (!pathKey) {
    return null
  }

  const [bucketName, filePath] = pathKey.split(':')

  let endPoint = 'https://s3.ap-northeast-2.amazonaws.com'
  if (process.env.VUE_APP_S3_ENDPOINT) {
    endPoint = process.env.VUE_APP_S3_ENDPOINT
  }

  let url = `${endPoint}/${bucketName}/${filePath}`

  if (timestamp) {
    url += `?v=${timestamp}`
  }
  return url
}

export function getS3CaptureDatetime (pathKey, utc = false) {
  if (!pathKey) {
    return null
  }

  const dateFragment = pathKey.split('/')
  if (dateFragment[1].length === 8) {
    let dateString = `${dateFragment[1].substr(0, 4)}-${dateFragment[1].substr(4, 2)}-${dateFragment[1].substr(6, 2)}T`
    if (dateFragment[2].length === 6) {
      dateString += `${dateFragment[2].substr(0, 2)}:${dateFragment[2].substr(2, 2)}:${dateFragment[2].substr(4, 2)}`
    } else {
      const nameFragment = dateFragment[4].split('-')
      if (nameFragment.length > 2) {
        const timeFragment = nameFragment[2].substr(0, 6)
        dateString += `${timeFragment.substr(0, 2)}:${timeFragment.substr(2, 2)}:${timeFragment.substr(4, 2)}`
      } else {
        dateString += '00:00:00'
      }
    }
    if (utc) {
      dateString += 'Z'
    }
    return dateString
  }
  return null
}

export function getS3CaptureType (pathKey) {
  const defaultType = 'default'
  if (!pathKey) {
    return null
  }
  const pathFragment = pathKey.split('/')
  const fileName = pathFragment[pathFragment.length - 1]
  const fileFragment = fileName.split('-')
  if (fileFragment.length < 3) {
    return defaultType
  }
  return fileFragment[1]
}

export function getCdnUrl (path) {
  path = path.replace(/^\/+/, '')
  return `${CDN_HOST}/dream/${path}`
}

export function getPercentage (value, total, digits) {
  if (isNaN(value) || isNaN(total) || total === 0) {
    return 0
  }

  let result = value / total * 100

  if (digits !== undefined) {
    result = Number(result.toFixed(digits))
  }
  return result
}

export function getPercentageInt (value, total, digits = 0, int = 0) {
  if (isNaN(value) || isNaN(total) || total === 0) {
    return 0
  }

  let result = value / total * 100
  result = Number(result)
  result = Number.isInteger(result) ? result.toFixed(int) : result.toFixed(digits)

  return result
}

export function fixedInt (value, digits, intDigits) {
  value = Number(Math.round(`${value}e${digits}`) + `e-${digits}`)
  return Number.isInteger(value) ? value.toFixed(intDigits) : value.toFixed(digits)
}

export function getTimeString (value) {
  let hour = Math.floor(value / 3600)
  let minute = Math.floor(value / 60) % 60
  let second = Math.round(value % 60)
  const locale = getI18n().locale

  if (second > 59) {
    second = 0
    minute++
  }
  if (minute > 59) {
    minute = 0
    hour++
  }
  if (hour >= 1) {
    return locale === 'ja' ? `${hour}時間 ${minute}分 ${second}秒` : `${hour}h ${minute}m ${second}s`
  }
  if (minute >= 1) {
    return locale === 'ja' ? `${minute}分 ${second}秒` : `${minute}m ${second}s`
  }
  return locale === 'ja' ? `${second}秒` : `${second}s`
}

export function getNumberWithOrdinal (n) {
  const s = ['th', 'st', 'nd', 'rd']
  const v = n % 100
  return n + (s[(v - 20) % 10] || s[v] || s[0])
}

export function thousand (v, fixed = 0) {
  const value = Number(v)
  if (isNaN(value)) {
    return v
  }
  return value.toLocaleString('en-US', { minimumFractionDigits: fixed, maximumFractionDigits: fixed })
}

export function leadingZero (number) {
  return number < 10 ? '0' + number : number
}

export function capitalize (string) {
  return string.charAt(0).toUpperCase() + string.slice(1)
}

export function getLocaleDateStr (value, defaultStr) {
  if (!value) {
    return defaultStr || null
  }

  const locale = getI18n().locale
  let formatStr

  switch (locale) {
    case 'en':
      formatStr = 'MMM D, YYYY'
      break
    case 'ko':
      formatStr = 'YYYY년 M월 D일'
      break
    case 'ja':
      formatStr = 'YYYY年MM月DD日'
      break
    default:
      throw new Error(`invalid locale: ${locale}`)
  }
  return format(parse(value), formatStr)
}

export function getRankStr (value) {
  if (typeof value !== 'number') {
    return value
  }

  const locale = getI18n().locale

  switch (locale) {
    case 'ko':
      return value + '위'
    case 'ja':
      return value + '位'
    default:
      if (value === 1) {
        return '1st'
      } else if (value === 2) {
        return '2nd'
      } else if (value === 3) {
        return '3rd'
      } else {
        return value + 'th'
      }
  }
}

export function getPluralString (value, str, prefix = 's') {
  if (value > 1) {
    return str + prefix
  }
  return str
}

export function getTimeParts (ms, secFixedNum = 0) {
  ms = ms || 0
  ms = Math.round(ms)

  let s = Math.floor(ms / 1000)
  let m = Math.floor(s / 60)
  s = s % 60

  if (secFixedNum > 0) {
    s = Number(((ms / 1000) % 60).toFixed(secFixedNum))
  }

  let h = Math.floor(m / 60)
  m = m % 60
  let d = Math.floor(h / 24)
  h = h % 24
  const parts = {
    d,
    h,
    m,
    s
  }
  return parts
}

export function getLocale () {
  const i18n = getI18n()

  if (i18n) {
    throw new Error('i18n not initialized')
  }
  return i18n.locale
}

export function getBrowserIconName (value) {
  value = String(value).toLowerCase().replace(/webview|browser|mobile|\s/g, '')
  switch (value) {
    case 'chrome':
    case 'edge':
    case 'facebook':
    case 'firefox':
    case 'ie':
    case 'safari':
    case 'opera':
    case 'android':
      return value
    default:
      return 'etc'
  }
}

export function getOsIconName (value) {
  value = String(value).toLowerCase().replace(/mobile|\s/g, '')
  switch (value) {
    case 'macos':
    case 'windows':
    case 'linux':
    case 'ios':
    case 'android':
      return value
    default:
      return 'etc'
  }
}

function getBrowserInfo () {
  const uaContains = (str) => window.navigator.userAgent.indexOf(str) !== -1
  let name = null
  let platform = null

  if (uaContains('Windows')) {
    platform = 'window'

    if (uaContains('MSIE') || uaContains('Trident/')) {
      name = 'ie'
    }
  }

  if (uaContains('Firefox')) {
    name = 'firefox'
  }

  if (uaContains('Chrome')) {
    name = 'chrome'
  }

  if (uaContains('Safari') && !uaContains('Chrome')) {
    name = 'safari'
  }

  if (uaContains('Edg')) {
    name = 'edge'
  }

  return {
    name,
    platform
  }
}

export const browserInfo = getBrowserInfo()

// https://codepen.io/code_monk/pen/FvpfI
export function getRandomHex (len) {
  const maxlen = 8
  const min = Math.pow(16, Math.min(len, maxlen) - 1)
  const max = Math.pow(16, Math.min(len, maxlen)) - 1
  const n = Math.floor(Math.random() * (max - min + 1)) + min
  let r = n.toString(16)
  while (r.length < len) {
    r = r + getRandomHex(len - maxlen)
  }
  return r
}

// https://stackoverflow.com/questions/38552003/how-to-decode-jwt-token-in-javascript-without-using-a-library
function b64DecodeUnicode (str) {
  return decodeURIComponent(
    Array.prototype.map.call(atob(str), c =>
      '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2)
    ).join(''))
}

export function parseJwt (token) {
  return JSON.parse(
    b64DecodeUnicode(
      token.split('.')[1].replace('-', '+').replace('_', '/')
    )
  )
}

export function loadScript (src) {
  return new Promise((resolve, reject) => {
    const id = `#${src}`

    if (document.getElementById(id)) {
      return resolve()
    }

    const el = document.createElement('script')
    el.src = src
    el.setAttribute('id', id)
    el.onload = resolve
    el.onerror = reject
    document.body.appendChild(el)
  })
}

export function getEncryptedUserNum (userNum) {
  if (!userNum) {
    throw new Error('userNum required')
  }
  return `${userNum}${getRandomHex(8)}`
}

export function triggerClickEvent () {
  let event

  if (typeof Event === 'function') {
    event = new Event('click')
  } else {
    event = document.createEvent('Event')
    event.initEvent('click', false, true)
  }
  document.body.dispatchEvent(event)
}

export function openBeusable () {
  const URL = `${beusableUrl}`
  let win = window.open(URL, isInIframe() ? '_self' : '_blank')
  win.focus()
}

export function openBeusableManagePlan () {
  const URL = `${beusableUrl}/user/manageplan`
  let win = window.open(URL, isInIframe() ? '_self' : '_blank')
  win.focus()
}

export function openGeoManagePlan () {
  const URL = `${geoUrl}/user/manageplan`
  let win = window.open(URL, isInIframe() ? '_self' : '_blank')
  win.focus()
}

export function goHome () {
  location.href = homeUrl
}

export function openPricing () {
  const URL = `${homeUrl}/userjourneymap/pricing`
  let win = window.open(URL, isInIframe() ? '_self' : '_blank')
  win.focus()
}

export function simpleUrl (value, matchType = 'simple', includeProtocol = false) {
  if (value && value.substr(0, 4) === 'http') {
    const url = document.createElement('a')
    url.href = value
    let link = ''
    if (matchType !== 'simple') {
      const hostStartIdx = url.href.indexOf(getDomainForView(url.host))
      link += (includeProtocol ? url.protocol + '//' : '') + url.href.slice(hostStartIdx)
    } else {
      const pathname = url.pathname.substr(0, 1) === '/' ? url.pathname : '/' + url.pathname
      link += (includeProtocol ? url.protocol + '//' : '') + getDomainForView(url.host) + pathname
    }
    let decodeLinkURL = ''
    try {
      decodeLinkURL = decodeURI(link)
    } catch (e) {
      decodeLinkURL = link
    }
    return decodeLinkURL
  }
  return value || ''
}

export function linkSet (value) {
  const url = document.createElement('a')
  url.href = value
  let linkSet = []
  linkSet.push(getDomainForView(url.host))
  linkSet.push(url.pathname)
  linkSet.push(url.protocol)
  return linkSet
}

export function byteLength (str) {
  const m = encodeURIComponent(str).match(/%[89ABab]/g)
  return str.length + (m ? m.length : 0)
}

export function characterWidth (str) {
  const m = encodeURIComponent(str).match(/%[89ABab]/g)
  return str.length + (m ? m.length / 2 : 0)
}

export function openContactUs (userData = null, content = null, adminPlan = false) {
  // let URL = `${homeUrl}/contactus`
  // if (userNum) {
  //   const key = getEncryptedUserNum(userNum)
  //   URL += `?key=${key}&type=pricing`
  // }
  // if (content) {
  //   URL += `&content=${encodeURI(content)}`
  // }
  // if (adminPlan) {
  //   URL += `&ba=true`
  // }
  // URL += '#contact'
  // let win = window.open(URL, '_blank')
  // win.focus()
  let sendData = {
    info: {
      email: userData.userId,
      organization: userData.userName,
      category: 'contactUs',
      lang: getI18n().locale,
      adminStatus: 'init'
    },
    content: content
  }
  if (content) sendData.contents = content
  let contactUs = document.createElement('div')
  let iframe = document.createElement('iframe')
  contactUs.classList.add('contactus-wrapper')
  iframe.classList.add('contactus-iframe')
  iframe.setAttribute('src', process.env.VUE_APP_SOLOMON_URL || 'https://solomon.beusable.net/beusChat')
  iframe.onload = () => {
    iframe.contentWindow.postMessage({ event: 'sendQuestion', msg: sendData }, '*')
  }
  contactUs.appendChild(iframe)
  document.body.appendChild(contactUs)

  window.addEventListener('message', (e) => {
    if (e.data.event === 'close') {
      contactUs.remove()
    }
  })
}

export function encrypt (data, key) {
  function padEnd (string, targetLength, padString) {
    targetLength = targetLength >> 0
    padString = String((typeof padString !== 'undefined' ? padString : ' '))
    if (string.length > targetLength) {
      return string.slice(0, 32)
    } else {
      targetLength = targetLength - string.length
      if (targetLength > padString.length) {
        padString += padString.repeat(targetLength / padString.length)
      }
      return string + padString.slice(0, targetLength)
    }
  }

  function textEncode (str) {
    if (window.TextEncoder) {
      return new TextEncoder('utf-8').encode(str)
    }
    let utf8 = unescape(encodeURIComponent(str))
    let result = new Uint8Array(utf8.length)
    for (let i = 0; i < utf8.length; i++) {
      result[i] = utf8.charCodeAt(i)
    }
    return result
  }

  const encodeBase64 = naclUtil.encodeBase64
  const nonce = nacl.randomBytes(24)
  const secretKey = textEncode(padEnd(key, 32, '0'))
  const secretData = textEncode(data)
  const encrypted = nacl.secretbox(secretData, nonce, secretKey)
  return `${encodeBase64(nonce)}:${encodeBase64(encrypted)}`
}

export function hexToRgb (hex) {
  var result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex.length < 6 ? hex + hex.replace('#', '') : hex)
  return result ? {
    r: parseInt(result[1], 16),
    g: parseInt(result[2], 16),
    b: parseInt(result[3], 16)
  } : { r: 0, g: 0, b: 0 }
}

export function componentToHex (c) {
  const hex = c.toString(16)
  return hex.length === 1 ? '0' + hex : hex
}

export function rgbToHex (r, g, b) {
  return '#' + componentToHex(r) + componentToHex(g) + componentToHex(b)
}

export function camelize (str) {
  if (!str) str = ''
  return str.toLowerCase().replace(/[^a-zA-Z0-9]+(.)/g, (m, chr) => chr.toUpperCase())
}

export function isSameDomain (from, target) {
  let url = document.createElement('a')
  url.href = from
  let valUrl = document.createElement('a')
  valUrl.href = target

  let result = url.host.split('.').reverse().join('.').indexOf(valUrl.host.split('.').reverse().join('.')) > -1

  url.remove()
  valUrl.remove()

  return result
}

export function urlSearchParam (url, name) {
  let results = new RegExp('[\?&]' + name + '=([^&#]*)').exec(url)
  if (results == null) {
    return null
  } else {
    return decodeURI(results[1]) || 0
  }
}

export function calculateLogDateTime (domain) {
  const logDateHourStr = domain.logDateHourStr
  const timezoneName = domain.timezoneName
  if (logDateHourStr && timezoneName) {
    const logDateTime = logDateHourStr + ':00'
    return new Date(logDateTime)
  } else {
    return new Date()
  }
}

export function calculateProcessDateTime (domain) {
  const processDateHourStr = domain.latestProcessHour
  const timezoneName = domain.timezoneName
  if (processDateHourStr && timezoneName) {
    const processDateTime = processDateHourStr + ':00'
    return new Date(processDateTime)
  } else {
    return new Date()
  }
}

export function dateFormatTimezone (value, timezoneName, formatString = 'YYYY-MM-DD') {
  return formatToTimeZone(value, formatString, { timeZone: timezoneName })
}

export function dateToLocalTimezone (value, timezoneName) {
  return convertToLocalTime(value, { timeZone: timezoneName })
}

export function utcToLocalTimezone (value, timezoneName) {
  return convertToTimeZone(value, { timeZone: timezoneName })
}

export function getOffset (timezoneName = DEFAULT_TIMEZONE_NAME) {
  const date = new Date()
  const utcDate = new Date(date.toLocaleString('en-US', { timeZone: 'UTC' }))
  const tzDate = new Date(date.toLocaleString('en-US', { timeZone: timezoneName }))
  let offset = (tzDate.getTime() - utcDate.getTime())
  let offset2 = offset / 60000
  let o1 = Math.abs(offset2)
  const offsetValue1 = (offset2 >= 0 ? '+' : '-') + ('00' + Math.floor(o1 / 60)).slice(-2) + ':' + ('00' + (o1 % 60)).slice(-2)
  return offsetValue1
}

export function getResampleReliability (origin, resample) {
  let computedRate = 0
  const absolute = Math.sqrt(Math.pow(origin - resample, 2))
  console.log('Reliability : ', origin, resample, absolute)
  console.log('origin : ', origin)
  console.log('resample : ', resample)
  if (absolute > origin) {
    return computedRate.toFixed(1)
  }
  let rate = (origin - absolute) / origin * 100
  computedRate = (Math.sqrt(Math.pow(rate, 2)))
  return computedRate.toFixed(1)
}

export function weekNumOfMonth (date) {
  const THURSDAY_NUM = 4
  const firstDate = new Date(date.getFullYear(), date.getMonth(), 1)
  const firstDayOfWeek = firstDate.getDay()

  let firstThursday = 1 + THURSDAY_NUM - firstDayOfWeek
  if (firstThursday <= 0) {
    firstThursday = firstThursday + 7
  }
  const untilDateOfFirstWeek = firstThursday - 7 + 3
  const weekNum = Math.ceil((date.getDate() - untilDateOfFirstWeek) / 7) - 1

  if (weekNum < 0) {
    const lastDateOfMonth = new Date(date.getFullYear(), date.getMonth(), 0)
    return weekNumOfMonth(lastDateOfMonth)
  }

  return format(date, 'YY-MM') + '-' + (weekNum + 1) + 'w'
}

export function isMatchedURL (host, pathName, targetUrl, matchType = null) {
  let isMatchedURL = false

  let parsedURL = new URL(targetUrl)

  const targetHost = parsedURL.host || ''
  let targetPathName = parsedURL.pathname || ''

  if (matchType && matchType === 'exact') {
    const pathStartIdx = parsedURL.href.indexOf(targetHost) + targetHost.length
    targetPathName = parsedURL.href.slice(pathStartIdx)
  }

  isMatchedURL = host === targetHost && pathName === targetPathName
  return isMatchedURL
}

let beuablyUrl = 'https://www.beusably.net'
if (process.env.VUE_APP_BEUSABLY_URL) {
  beuablyUrl = process.env.VUE_APP_BEUSABLY_URL
}
export const BEUSABLY_URL = beuablyUrl

export function getUtmString (from, ids, tag) {
  const fromObj = {}
  for (const f of from) {
    const fs = Object.entries(f)[0]
    fromObj[fs[1]] = fs[0]
  }

  const values = []
  for (const id of ids) {
    if (id === null) {
      values.push('Inflow without utm_' + tag + ' parameter')
    } else if (id) {
      values.push(fromObj[id])
    } else {
      values.push('utm_' + tag + '="" or utm_' + tag)
    }
  }
  return values.sort((a, b) => {
    if (a.includes('Inflow without utm_')) {
      return -1
    } else if (b.includes('Inflow without utm_')) {
      return 1
    } else {
      return a.localeCompare(b)
    }
  }).join(', ')
}

export function getOrderId (userNum) {
  const date = new Date()
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  const hours = String(date.getHours()).padStart(2, '0')
  const minutes = String(date.getMinutes()).padStart(2, '0')
  const dateFormat = `${year}${month}${day}_${hours}${minutes}`
  const orderId = 'PAY_NOW_' + userNum + '_' + dateFormat
  return orderId
}

export function getDomainForView (value) {
  let domain = value.trim().replace('http://', '').replace('https://', '').replace(/\//g, '')

  if (domain.startsWith('xn--') > -1) {
    // Punycode → 한글 도메인 변환
    return punycode.toUnicode(domain)
  } else {
    // 한글 도메인 또는 영어 도메인은 그대로 유지
    return value
  }
}

export function getDomainForData (value) {
  let domain = value.trim()
  if (/[\u3131-\uD79D]/.test(domain)) {
    // 한글이 포함된 경우 Punycode 변환
    return punycode.toASCII(domain)
  } else {
    // 한글이 없는 경우 그대로 유지
    return domain
  }
}

function rehypeWrapTable () {
  return (tree) => {
    visit(tree, 'element', (node, index, parent) => {
      if (node.tagName === 'table' && parent) {
        parent.children[index] = {
          type: 'element',
          tagName: 'div',
          properties: { className: ['table-wrap'] },
          children: [node]
        }
      }
    })
  }
}

export const convertMarkdownToHtml = (markDownText) => {
  const htmlText = unified()
    .use(markdown)
    .use(remarkGfm, { singleTilde: false })
    .use(remark2rehype)
    .use(rehypePrismPlus)
    .use(rehypeExternalLinks, { rel: ['nofollow'], target: '_blank' })
    .use(rehypeWrapTable)
    .use(html)
    .processSync(markDownText)
  return htmlText
}

export const createAuthUrl = (baseUrl) => {
  if (!baseUrl) return baseUrl

  // iframe이 아닐 때는 쿠키를 사용할 수 있으므로 URL 파라미터 추가 불필요
  if (!isInIframe()) {
    return baseUrl
  }

  const token = CommonStorage.getItem('_beu_a_t_')
  const locale = CommonStorage.getItem('locale')

  try {
    const url = new URL(baseUrl)
    if (token) url.searchParams.set('_beu_a_t_', token)
    if (locale) url.searchParams.set('locale', locale)

    return url.toString()
  } catch (e) {
    console.warn('Invalid URL:', baseUrl, e)
    return baseUrl
  }
}
